import { useState, useEffect } from 'react';

export const useExitIntent = () => {
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  // LocalStorage keys
  const DONT_SHOW_KEY = 'dontShowExit';
  const LAST_TIME_KEY = 'exitIntentLastTime';

  // Helper: get localStorage value
  const getFlag = (key: string): string | null => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  // Helper: set localStorage value
  const setFlag = (key: string, value: string) => {
    try {
      window.localStorage.setItem(key, value);
    } catch {}
  };

  // Helper: check if more than 7 days passed
  const isExpired = (last: string | null) => {
    if (!last) return true;
    const lastTime = Number(last);
    if (isNaN(lastTime)) return true;
    return Date.now() - lastTime > 7 * 24 * 60 * 60 * 1000;
  };

  useEffect(() => {
    // Ensure lastTimeAccess is set on app load
    if (!getFlag(LAST_TIME_KEY)) {
      setFlag(LAST_TIME_KEY, String(Date.now()));
    }

    const handleMouseLeave = (e: MouseEvent) => {
      const dontShow = getFlag(DONT_SHOW_KEY) === 'true';
      const lastTime = getFlag(LAST_TIME_KEY);
      if (e.clientY <= 0 && !hasShown && !dontShow) {
        if (isExpired(lastTime)) {
          setFlag(LAST_TIME_KEY, String(Date.now()));
          setShowExitIntent(true);
          setHasShown(true);
        }
      }
    };

    const handleBeforeUnload = () => {
      const dontShow = getFlag(DONT_SHOW_KEY) === 'true';
      const lastTime = getFlag(LAST_TIME_KEY);
      if (!hasShown && !dontShow) {
        if (isExpired(lastTime)) {
          setFlag(LAST_TIME_KEY, String(Date.now()));
          setShowExitIntent(true);
          setHasShown(true);
        }
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasShown]);

  // Called when form is submitted
  const setDontShowExit = () => {
    setFlag(DONT_SHOW_KEY, 'true');
    setShowExitIntent(false);
  };

  // Called when modal is closed without submit
  const closeExitIntent = () => {
    setShowExitIntent(false);
    setFlag(LAST_TIME_KEY, String(Date.now()));
  };

  return { showExitIntent, closeExitIntent, setDontShowExit };
};